import { useEffect, useState } from "react";
import { gameApi } from "@/lib/api";
import { Loader2, AlertTriangle, ChevronDown, ChevronRight, ShieldAlert, X, CheckCircle2 } from "lucide-react";

const n = (v) => (v ?? 0).toLocaleString();
const dt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

const Stat = ({ label, value, tone }) => (
  <div className="rounded-xl border border-border bg-card px-3 py-2">
    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className={`text-lg font-bold ${tone || "text-primary"}`}>{value}</p>
  </div>
);

const PlayerRow = ({ p }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`glitch-player-${p.player_number}`} className="rounded-xl border border-border bg-card">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 px-3 py-2 text-left">
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <span className="font-bold text-primary">#{p.player_number} {p.display_name}</span>
        {p.already_corrected && <span className="rounded-full bg-green-100 px-1.5 text-[10px] font-bold text-green-700">corrected</span>}
        {p.uncertain && <span className="rounded-full bg-amber-100 px-1.5 text-[10px] font-bold text-amber-700">review</span>}
        <span className={`ml-auto text-sm font-bold ${p.already_corrected ? "text-muted-foreground line-through" : "text-rose-600"}`}>−{n(p.total_overcredit)}🪙</span>
      </button>
      {open && (
        <div className="space-y-2 border-t border-border px-3 py-2 text-xs">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat label="Current currency" value={n(p.current_currency)} />
            <Stat label="Current coins_earned" value={n(p.current_coins_earned)} />
            <Stat label="Proposed currency" value={n(p.proposed_currency)} tone="text-green-700" />
            <Stat label="Proposed coins_earned" value={n(p.proposed_coins_earned)} tone="text-green-700" />
          </div>
          <p className="text-[11px] text-muted-foreground">
            {p.duplicate_event_count} duplicate event(s) · first {dt(p.first_at)} · last {dt(p.last_at)}
          </p>
          {[...p.regular_shows.map((s) => ({ ...s, _t: "Show" })), ...p.barn_shows.map((s) => ({ ...s, _t: "Barn Show" }))].map((s, i) => (
            <div key={i} className="rounded-lg border border-border bg-background px-2 py-1.5">
              <span className="font-semibold">{s._t}: {s.horse_name || s.show_id} · {s.discipline || ""} {s.kind || ""} · {s.placing}° place</span>
              <div className="text-[11px] text-muted-foreground">
                legit {n(s.legit_payout)}🪙 × found {s.records_found} → {s.duplicate_events} extra = <span className="font-bold text-rose-600">−{n(s.extra_coins)}🪙</span> · {dt(s.first_at)}–{dt(s.last_at)}
              </div>
            </div>
          ))}
          {p.notes?.map((nt, i) => (
            <p key={i} className="flex items-start gap-1 text-[11px] text-amber-700"><AlertTriangle size={12} className="mt-0.5 shrink-0" />{nt}</p>
          ))}
        </div>
      )}
    </div>
  );
};

const BarnRow = ({ b }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`glitch-barn-${b.tag}`} className="rounded-xl border border-border bg-card">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 px-3 py-2 text-left">
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <span className="font-bold text-primary">{b.name} [{b.tag}]</span>
        <span className="ml-auto text-sm font-bold text-rose-600">−{n(b.overcredit_lifetime)} pts</span>
      </button>
      {open && (
        <div className="space-y-2 border-t border-border px-3 py-2 text-xs">
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead><tr className="text-muted-foreground"><th className="text-left">Metric</th><th className="text-right">Current</th><th className="text-right">Over-credit</th><th className="text-right">Reconstructed</th></tr></thead>
              <tbody>
                {[["Overall (lifetime) points", "current_lifetime_points", "overcredit_lifetime", "reconstructed_lifetime_points"],
                  ["Monthly points", "current_monthly_points", "overcredit_monthly", "reconstructed_monthly_points"],
                  ["Weekly points", "current_weekly_points", "overcredit_weekly", "reconstructed_weekly_points"],
                  ["1st-place wins", "current_first_place_wins", null, "reconstructed_first_place_wins"],
                  ["Total placings", "current_total_placings", null, "reconstructed_total_placings"]].map(([lbl, c, o, r]) => (
                  <tr key={lbl} className="border-t border-border/50">
                    <td className="py-1">{lbl}</td>
                    <td className="text-right">{n(b[c])}</td>
                    <td className="text-right text-rose-600">{o ? `−${n(b[o])}` : "—"}</td>
                    <td className="text-right font-bold text-green-700">{n(b[r])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-muted-foreground">Account balance: {n(b.account_balance)}🪙 · {b.duplicate_prize_payouts.length} duplicate barn-show payout group(s)</p>
          {b.notes?.map((nt, i) => (
            <p key={i} className="flex items-start gap-1 text-[11px] text-amber-700"><AlertTriangle size={12} className="mt-0.5 shrink-0" />{nt}</p>
          ))}
        </div>
      )}
    </div>
  );
};

const Board = ({ title, current, proposed }) => (
  <div className="rounded-xl border border-border bg-card p-3">
    <h4 className="mb-2 font-display font-bold text-primary">{title}</h4>
    <div className="grid grid-cols-2 gap-3 text-[11px]">
      {[["Current", current], ["Proposed (after correction)", proposed]].map(([lbl, list]) => (
        <div key={lbl}>
          <p className="mb-1 font-bold text-muted-foreground">{lbl}</p>
          {list.map((r, i) => (
            <div key={i} className={`flex items-center gap-1 rounded px-1 py-0.5 ${r.affected ? "bg-amber-50" : ""}`}>
              <span className="w-6 text-right text-muted-foreground">{lbl === "Current" ? r.current_rank : r.proposed_rank}.</span>
              <span className="truncate">#{r.player_number} {r.display_name}</span>
              <span className="ml-auto font-semibold">{n(lbl === "Current" ? r.current_value : r.proposed_value)}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  </div>
);

const ApplyPanel = ({ since, onClose, onApplied }) => {
  const [plan, setPlan] = useState(null);
  const [err, setErr] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const qs = since ? `?since=${encodeURIComponent(since)}` : "";
        const { data } = await gameApi.get(`/game/admin/glitch-correction-plan${qs}`);
        setPlan(data);
      } catch (e) { setErr(e.response?.data?.detail || "Failed to load plan"); }
    })();
  }, [since]);

  const run = async () => {
    setBusy(true); setErr(null);
    try {
      const { data } = await gameApi.post("/game/admin/glitch-correct", { confirm_text: confirm, since: since || null });
      setResult(data);
    } catch (e) { setErr(e.response?.data?.detail || "Correction failed"); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4" data-testid="glitch-apply-modal">
      <div className="flex max-h-[86vh] w-full max-w-2xl flex-col rounded-2xl border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h3 className="font-display text-lg font-bold text-destructive">Apply Player Glitch Corrections</h3>
          <button data-testid="glitch-apply-cancel" onClick={onClose} className="rounded-full p-1 hover:bg-black/5"><X size={18} /></button>
        </div>
        <div className="flex-1 space-y-3 overflow-y-auto p-4 text-sm">
          {result ? (
            <div data-testid="glitch-apply-result" className="space-y-2">
              <p className="flex items-center gap-2 font-bold text-green-700"><CheckCircle2 size={18} /> Corrections applied</p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Stat label="Players corrected" value={n(result.players_corrected)} />
                <Stat label="Currency removed" value={n(result.total_currency_removed)} tone="text-rose-600" />
                <Stat label="coins_earned removed" value={n(result.total_coins_earned_removed)} tone="text-rose-600" />
                <Stat label="Floored at 0" value={n(result.accounts_floored_at_zero)} />
              </div>
              <p className="text-[11px] text-muted-foreground">Batch {result.batch_id} · by {result.admin} · {dt(result.applied_at)} · {result.already_corrected_skipped} already-corrected skipped</p>
              <button data-testid="glitch-apply-done" onClick={onApplied} className="w-full rounded-lg bg-primary px-3 py-2 font-bold text-primary-foreground">Done — refresh diagnostic</button>
            </div>
          ) : !plan ? (
            <div className="flex items-center gap-2 py-8 text-muted-foreground"><Loader2 className="animate-spin" size={16} /> Loading plan…</div>
          ) : (
            <>
              <div className="rounded-xl border-2 border-destructive/40 bg-destructive/5 p-3">
                <p className="font-bold text-destructive">This will WRITE to player data — currency & coins_earned only. Barns are NOT touched.</p>
                <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <Stat label="Players to correct" value={n(plan.summary.players_to_correct)} />
                  <Stat label="Currency to remove" value={n(plan.summary.total_currency_to_remove)} tone="text-rose-600" />
                  <Stat label="coins_earned to remove" value={n(plan.summary.total_coins_earned_to_remove)} tone="text-rose-600" />
                  <Stat label="Floored at 0" value={n(plan.summary.accounts_floored_at_zero)} />
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">{plan.summary.already_corrected_skipped} already-corrected player(s) will be skipped (idempotent — safe to run once).</p>
              </div>
              <div className="max-h-56 space-y-1 overflow-y-auto">
                {plan.players.map((p) => (
                  <div key={p.player_id} data-testid={`glitch-plan-${p.player_number}`} className="flex items-center gap-2 rounded-lg border border-border bg-background px-2 py-1 text-xs">
                    <span className="font-semibold">#{p.player_number} {p.display_name}</span>
                    {p.floored && <span className="rounded bg-amber-100 px-1 text-[10px] font-bold text-amber-700">floored</span>}
                    <span className="ml-auto text-muted-foreground">{n(p.current_currency)}→<b className="text-foreground">{n(p.proposed_currency)}</b> · ce {n(p.current_coins_earned)}→<b className="text-foreground">{n(p.proposed_coins_earned)}</b> · <span className="text-rose-600">−{n(p.over_credit)}</span></span>
                  </div>
                ))}
                {plan.players.length === 0 && <p className="py-4 text-center text-muted-foreground">Nothing pending — all detected corrections are already applied.</p>}
              </div>
              {plan.players.length > 0 && (
                <div className="space-y-2 border-t border-border pt-3">
                  <label className="block text-xs font-bold">Type <span className="font-mono text-destructive">APPLY</span> to confirm this write:</label>
                  <input data-testid="glitch-apply-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" placeholder="APPLY" />
                  <button data-testid="glitch-apply-run" disabled={busy || confirm.trim().toUpperCase() !== "APPLY"} onClick={run}
                    className="w-full rounded-lg bg-destructive px-3 py-2 font-bold text-white disabled:opacity-40">
                    {busy ? "Applying…" : "Confirm & Apply Corrections"}
                  </button>
                </div>
              )}
            </>
          )}
          {err && <p className="text-sm font-bold text-destructive">{err}</p>}
        </div>
      </div>
    </div>
  );
};

export const GlitchDiagnostic = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [since, setSince] = useState("");
  const [showApply, setShowApply] = useState(false);

  const load = async () => {
    setData(null); setErr(null);
    try {
      const qs = since ? `?since=${encodeURIComponent(since)}` : "";
      const { data } = await gameApi.get(`/game/admin/glitch-diagnostic${qs}`);
      setData(data);
    } catch (e) { setErr(e.response?.data?.detail || "Failed to run diagnostic"); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div className="space-y-3" data-testid="glitch-diagnostic">
      <div className="flex items-center gap-2 rounded-xl border-2 border-amber-400 bg-amber-50 px-3 py-2">
        <ShieldAlert className="shrink-0 text-amber-600" size={20} />
        <p className="font-display text-sm font-bold text-amber-800">READ ONLY — GLITCH CORRECTION DIAGNOSTIC — NO DATA HAS BEEN CHANGED</p>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="block text-[11px] font-bold text-muted-foreground">Only events since (optional, ISO)</label>
          <input data-testid="glitch-since" value={since} onChange={(e) => setSince(e.target.value)}
            placeholder="2026-08-20T16:00:00Z" className="rounded-lg border border-border bg-background px-2 py-1.5 text-xs" />
        </div>
        <button data-testid="glitch-run" onClick={load} className="rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground">Run diagnostic</button>
      </div>

      {err && <p className="text-sm font-bold text-destructive">{err}</p>}
      {!data && !err && <div className="flex items-center gap-2 py-8 text-muted-foreground"><Loader2 className="animate-spin" size={16} /> Scanning payout records…</div>}

      {data && (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
            <Stat label="Affected players" value={n(data.summary.affected_players)} />
            <Stat label="Excess currency" value={n(data.summary.total_excess_currency)} tone="text-rose-600" />
            <Stat label="Excess coins_earned" value={n(data.summary.total_excess_coins_earned)} tone="text-rose-600" />
            <Stat label="Affected barns" value={n(data.summary.affected_barns)} />
            <Stat label="Excess barn points" value={n(data.summary.total_excess_barn_points)} tone="text-rose-600" />
            <Stat label="Flagged for review" value={n(data.summary.uncertain_records.length)} tone="text-amber-700" />
          </div>

          {data.summary.affected_players > 0 && (
            <button data-testid="glitch-open-apply" onClick={() => setShowApply(true)}
              className="w-full rounded-xl bg-destructive px-3 py-2 text-sm font-bold text-white transition-opacity hover:opacity-90">
              Review &amp; Apply Player Corrections (currency &amp; coins_earned only — barns untouched)
            </button>
          )}

          <h3 className="pt-2 font-display text-base font-bold text-primary">Affected players ({data.players.length})</h3>
          {data.players.length === 0 ? <p className="text-sm text-muted-foreground">No duplicate player payouts detected.</p>
            : <div className="space-y-1.5">{data.players.map((p) => <PlayerRow key={p.player_id} p={p} />)}</div>}

          <h3 className="pt-2 font-display text-base font-bold text-primary">Affected barns ({data.barns.length})</h3>
          {data.barns.length === 0 ? <p className="text-sm text-muted-foreground">No barn-point inflation detected.</p>
            : <div className="space-y-1.5">{data.barns.map((b) => <BarnRow key={b.barn_id} b={b} />)}</div>}

          <h3 className="pt-2 font-display text-base font-bold text-primary">Leaderboard impact (current vs proposed)</h3>
          <Board title="Most Coins Earned" current={data.ranking_impact.most_coins_earned.current} proposed={data.ranking_impact.most_coins_earned.proposed} />
          <Board title="Overall Ranking" current={data.ranking_impact.overall.current} proposed={data.ranking_impact.overall.proposed} />

          {data.summary.uncertain_records.length > 0 && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-3">
              <h4 className="mb-1 flex items-center gap-1 font-bold text-amber-800"><AlertTriangle size={14} /> Flagged for manual review</h4>
              <pre className="whitespace-pre-wrap break-all text-[11px] text-amber-900">{JSON.stringify(data.summary.uncertain_records, null, 2)}</pre>
            </div>
          )}

          <p className="pt-2 text-center text-[11px] font-bold text-amber-700">READ ONLY — NO DATA HAS BEEN CHANGED · generated {dt(data.generated_at)}</p>
          {showApply && <ApplyPanel since={since} onClose={() => setShowApply(false)} onApplied={() => { setShowApply(false); load(); }} />}
        </>
      )}
    </div>
  );
};
